import type { Logger } from './types'
import type { Project } from '@scalar-org/entities/project'
import type { Theme } from '@scalar-org/entities/theme'
import { parseAnything } from '@scalar-org/helpers/parse'
import { themePresets } from '@scalar-org/library/themes'
import { getPageMeta, type PageMeta } from '@scalar-org/scalar-config'
import {
  resolveSyncProject,
  scalarConfigSchema,
} from '@scalar-org/scalar-config'
import type { FSWatcher } from 'chokidar'
import fs from 'node:fs/promises'
import { join, dirname, isAbsolute } from 'node:path'
import { debounce } from '@scalar-org/helpers/events'

type ResolvedProject = {
  project: Project
  documents: Record<string, string>
  references: Record<string, string>
  documentPaths: Record<string, string>
  theme: Theme
}

/**
 * When watching the config we need to handle three basic events:
 * 1. The config file has changed
 * 2. The config file has errors
 * 3. A file in the config has changed
 */
export async function watchConfigFiles({
  configPath,
  watcher,
  onConfigChange,
  onConfigError,
  onFileChange,
  logger,
}: {
  configPath: string
  watcher: FSWatcher
  onConfigChange: (data: Omit<ResolvedProject, 'documentPaths'>) => void
  onConfigError: (errors: string[]) => void
  onFileChange: (data: {
    uid: string
    content: string
    meta: PageMeta
  }) => void
  logger: Logger
}) {
  /**
   * A map of record uids to their paths
   *
   * Allows us to emit a content event specific to a document.
   * Client-side the documents/references objects are keyed by uid
   */
  const uidsToPaths: Record<string, string> = {}
  const configDir = dirname(configPath)

  /** Save the path of the UID for lookup on changes.  */
  function mapUids(documentPaths: ResolvedProject['documentPaths']) {
    Object.keys(documentPaths).forEach((path) => {
      uidsToPaths[join(configDir, documentPaths[path])] = path
    })
  }

  /** Parse the initial config file and load the state */
  const initialConfig = await parseConfig(configPath, watcher, logger)
  if (initialConfig.success) {
    const { documentPaths, ...rest } = initialConfig.data
    mapUids(documentPaths)
    onConfigChange(rest)
  }

  /** Reparse the entire state and update the maps when the config file changes */
  watcher.on(
    'change',
    debounce(async (path) => {
      if (path === configPath) {
        const parsed = await parseConfig(path, watcher, logger)

        // Save the map of record uids to their paths
        if (parsed.success) {
          const { documentPaths, ...rest } = parsed.data
          mapUids(documentPaths)
          return onConfigChange(rest)
        }

        return onConfigError(parsed.errors)
      }

      const fileContent = await fs.readFile(path, 'utf8')

      /**
       * Parse any frontmatter and content from the file
       *
       * We will hot-reload the page definition if there is any metadata changes (like title or description)
       */
      const { content, meta } = getPageMeta(fileContent)

      onFileChange({
        uid: uidsToPaths[path],
        content,
        meta,
      })
    }, 500),
  )

  return watcher
}

/**
 * Parse the config file and resolve the project
 *
 * If everything is valid we return the project and document records
 */
export async function parseConfig(
  configPath: string,
  watcher: FSWatcher,
  logger: Logger,
): Promise<
  | {
      success: true
      data: ResolvedProject
    }
  | { success: false; errors: string[] }
> {
  const configDir = dirname(configPath)
  const parsed = parseAnything(
    await fs.readFile(configPath, 'utf8'),
    scalarConfigSchema,
  )

  if (!parsed.success) {
    return {
      success: false,
      errors: parsed.issues?.map((issue) => issue.message) ?? [],
    }
  }

  // TODO: Handle fetching the theme from the server if a tag is provided
  const theme =
    themePresets.find((t) => t.slug === parsed.data.siteConfig.theme) ??
    themePresets[0]

  const resolved = await resolveSyncProject({
    config: parsed.data,
    fileGetter: async ({ path }) => {
      const absolutePath = isAbsolute(path) ? path : join(configDir, path)
      return fs.readFile(absolutePath, 'utf-8')
    },
    folderGetter: async (_path: string) => {
      const path = isAbsolute(_path) ? _path : join(configDir, _path)
      const content = await fs.readdir(path, { withFileTypes: true })

      return content
        .filter((e) => e.isFile() || e.isDirectory())
        .map((e) => {
          return {
            name: e.name,
            type: e.isFile() ? 'file' : 'dir',
            path: join(path, e.name),
          }
        })
    },
    theme,
    parallel: true,
  })
    .then((data) => ({
      success: true as const,
      data,
    }))
    .catch((err) => {
      logger('Uncaught parse error', 'error', { error: err })
      return {
        success: false as const,
        errors: [
          'Unable to properly resolve the project. Check that all filepaths are valid',
        ],
      }
    })

  if (resolved.success) {
    Object.values(resolved.data.documentPaths).forEach((path) => {
      watcher.add(join(configDir, path))
    })
  }

  return resolved
}
